/**
 * Data processors for Drupal decoupled_toolbox API responses.
 *
 * The decoupled API returns items as:
 *   [ { item_url, submission_url, delete_url, data: { ... } }, ... ]
 *
 * These processors extract the `data` objects and remap field names
 * to match what the React front-end components expect.
 *
 * The taxonomy-to-options processor converts taxonomy term collections
 * into SelectField-compatible options arrays.
 */

import { decodeHtmlEntities } from '../utils/html';

/**
 * Opt-out marker a caller puts in its URL to get the decoupled payload RAW.
 *
 * The processors below are registered globally and match on URL, so a page
 * that reads a collection these processors also match gets the reshaped
 * result whether it wants it or not. That is right for the many screens that
 * only need `{value, label}` options — and wrong for a screen that EDITS the
 * entities, which needs the Syncable Object itself (submission_url /
 * delete_url / data) to write it back.
 *
 * Adding `rawSyncable=1` to the request URL says "leave this response alone".
 * Drupal ignores the unknown parameter, so the URL stays a plain decoupled
 * collection.
 */
export const RAW_SYNCABLE_PARAM = 'rawSyncable=1';

export const wantsRawSyncable = (url) => String(url ?? '').includes(RAW_SYNCABLE_PARAM);

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
};

/**
 * Champs d'un item decoupled, quelle que soit la forme reçue.
 *
 * decoupled_toolbox n'enveloppe l'item dans `data:` que si le LECTEUR peut
 * ÉCRIRE l'entité (`RequestEntity::structureEntityOutput` : l'enveloppe et les
 * `submission_url` / `delete_url` vont ensemble). Un membre en lecture seule
 * reçoit les champs À PLAT, à côté d'`item_url`.
 *
 * Filtrer une collection sur `item.data` jetait donc TOUTES les lignes d'un
 * lecteur sans droit d'écriture — sans erreur, sans réponse en échec : liste de
 * projets vide et kanban vide alors que le serveur avait répondu 200 avec les
 * données. Tout processor doit accepter les deux formes.
 */
const fieldsOf = (item) => item?.data ?? item;

/** Une ligne decoupled exploitable, enveloppée ou à plat. */
const isDecoupledRow = (item) => !!item && typeof item === 'object'
  && (item.data != null || item.id != null);

// Label → frontend column ID (1-9 for main tasks, 101-104 for sub-tasks).
// These maps are label-based and therefore survive DB resets (TIDs can change,
// labels are stable). TID resolution is handled dynamically by _tidToLabel below.
// TODO Phase 2: once field_kanban_column and field_kanban_sub_column are exposed
// by the Drupal task_status taxonomy API, these label maps can be removed and
// column IDs can be read directly from the API response via taskStatusMapsProcessor.
const STATUS_COLUMN_MAP = {
  'Annulé': 1,
  'Backlog': 2,
  'Demande': 3,
  'Prêt à développer': 4,
  'En cours': 5,
  'Développé': 6,
  'Validé': 7,
  'Terminé': 8,
  'Bloqué': 9,
};

const SUB_STATUS_COLUMN_MAP = {
  'Prêt à développer': 101,
  'Backlog': 101,
  'Demande': 101,
  'En cours': 102,
  'Développé': 103,
  'Terminé': 103,
  'Validé': 103,
  'Bloqué': 104,
  'Annulé': 104,
};

// Legacy TID-based maps kept as last-resort fallbacks only.
// These break on DB resets because Drupal auto-increments TIDs.
// The dynamic _tidToLabel cache built by taskStatusMapsProcessor takes priority.
const STATUS_TID_TO_LABEL = {
  47: 'Annulé', 48: 'Backlog', 49: 'Demande', 50: 'Prêt à développer',
  51: 'En cours', 52: 'Développé', 53: 'Validé', 54: 'Terminé', 55: 'Bloqué',
};

// Dynamic TID → label cache, populated at runtime from the task_status API.
// Keys are strings (matching d.id from the API response, e.g. "62").
// Null until taskStatusMapsProcessor has run.
let _tidToLabel = null;


/**
 * Populate the module-level _tidToLabel cache from the task_status taxonomy API.
 *
 * Must be the first data source fetched (before node/task) so that tasksProcessor
 * can resolve TIDs to labels correctly regardless of DB state.
 *
 * Handles both raw format {id, name} and transformed format {value, label} so it
 * works regardless of its position relative to taxonomyToOptionsProcessor in the
 * processor chain.
 *
 * TODO Phase 2: when field_kanban_column and field_kanban_sub_column are exposed
 * by Drupal, also cache _tidToColumn and _tidToSubColumn here and update
 * resolveStatus() to use them instead of STATUS_COLUMN_MAP / SUB_STATUS_COLUMN_MAP.
 */
export const taskStatusMapsProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/taxonomy_term/task_status/')) return dataToProcess;
  if (!requestContext.url.includes('/collection')) return dataToProcess;
  if (responseContext.status >= 400 || !Array.isArray(dataToProcess)) return dataToProcess;

  _tidToLabel = {};
  dataToProcess.forEach((item) => {
    const d = item.data ?? item;
    const tid = String(d.id ?? d.tid ?? d.value ?? '');
    const label = d.name || d.label || '';
    if (tid && label) _tidToLabel[tid] = label;
  });

  return dataToProcess;
};

/**
 * Transform a single project response (e.g. /decoupled-api/group/project/11)
 * into a root-mergeable object for the kanban/task-edit pages.
 * Runs BEFORE projectsProcessor so the raw API data is still intact.
 */
export const singleProjectProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!/\/decoupled-api\/group\/project\/\d+/.test(requestContext.url)) return dataToProcess;
  if (requestContext.url.includes('/collection')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;
  if (!isDecoupledRow(dataToProcess)) return dataToProcess;

  const d = fieldsOf(dataToProcess);
  // hide_estimation lives inside the project_settings JSON string (no dedicated
  // field); enabledTaskTypes and workingHours have their own decoupled fields.
  // Parse the JSON so load mirrors what the save path (KanbanHeader) writes.
  // The rights matrix is NOT here: it lives in the project group permissions
  // (GET /api/project/{id}/rights), read by ProjectSettingsModal.
  let ps = {};
  try { ps = d.projectSettings ? (JSON.parse(d.projectSettings) || {}) : {}; } catch { ps = {}; }
  return {
    pageTitle: d.name || '',
    project: {
      id: d.id,
      name: d.name,
      description: stripHtml(d.description),
      picto: d.picto || '📁',
      // Édités depuis la pop-in de configuration, au même titre que le titre :
      // absents d'ici, ils s'y afficheraient vides et le premier enregistrement
      // les écraserait.
      methodology: d.methodology || '',
      account: d.account || '',
      // Items objets `{ value }`, repris TELS QUELS : c'est ce que la pop-in
      // édite et ce que le back attend en retour.
      hosts: Array.isArray(d.hosts) ? d.hosts : [],
      // Adresse de l'instance de l'agent IA, éditée dans la même pop-in.
      agentInstance: d.agentInstance || '',
      archived: d.archived || null,
      // Numeric IDs of the users who are members of (have access to) the project.
      // Used to scope the assignee field + kanban assignee filter to members.
      memberIds: (Array.isArray(d.memberIds) ? d.memberIds : []).map((id) => parseInt(id, 10)).filter(Number.isFinite),
      // Raw JSON exposed so the settings save path can merge onto it.
      projectSettings: d.projectSettings || '',
    },
    settings: {
      enabledTaskTypes: mapEnabledTaskTypes(d.enabledTaskTypes),
      workingHours: d.workingHours || {},
      hideEstimation: !!ps.hide_estimation_from_client,
    },
  };
};

/**
 * Transform /decoupled-api/user/user/collection → users array.
 * The user API returns flat objects (no item_url/data wrapper).
 */
export const usersProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/user/user/')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;
  if (!Array.isArray(dataToProcess)) return dataToProcess;

  return dataToProcess
    .filter((item) => item != null)
    .map((item) => {
      const u = item.data ?? item;
      if (!u.id || u.id === 0 || u.active === false) return null;
      return {
        id: u.id,
        name: u.name || '',
        email: u.email || '',
        // Pas de photo : l'UI affiche une pastille d'initiales (recette `.avatar` du DS).
        roles: Array.isArray(u.roles) ? u.roles : [],
        dailyCapacity: u.dailyCapacity ?? 7.0,
        workSchedule: parseWorkSchedule(u.workSchedule),
      };
    })
    .filter(Boolean);
};

function parseWorkSchedule(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Transform /decoupled-api/group/project/collection → projects array
 */
export const projectsProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/group/project/')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;

  if (Array.isArray(dataToProcess)) {
    return dataToProcess
      .filter(isDecoupledRow)
      .map((item) => mapProject(item));
  }

  if (isDecoupledRow(dataToProcess)) {
    return mapProject(dataToProcess);
  }

  return dataToProcess;
};

function mapProject(item) {
  const d = fieldsOf(item);
  const memberIds = (Array.isArray(d.memberIds) ? d.memberIds : []).map((id) => parseInt(id, 10));
  return {
    id: d.id,
    name: d.name,
    description: stripHtml(d.description),
    client: d.account || '',
    methodology: d.methodology || '',
    picto: d.picto || '📁',
    // T38: decoupled_file exposes the image as { url }; keep a plain URL string.
    pictoImage: (d.pictoImage && typeof d.pictoImage === 'object') ? (d.pictoImage.url || '') : (d.pictoImage || ''),
    isTemplate: d.isTemplate || false,
    archived: d.archived || null,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    enabledTaskTypes: mapEnabledTaskTypes(d.enabledTaskTypes),
    workingHours: d.workingHours || {},
    // Domains claimed by this project, as the native `{value}` items the back
    // renders and expects. This mapper is an explicit allow-list: a field left
    // out here is silently dropped, however well the API exposes it.
    hosts: Array.isArray(d.hosts) ? d.hosts : [],
    agentInstance: d.agentInstance || '',
    projectSettings: d.projectSettings || '',
    workflows: d.workflows || {},
    memberIds,
    users: [],
    _meta: {
      itemUrl: item.item_url,
      submissionUrl: item.submission_url,
      deleteUrl: item.delete_url,
    },
  };
}

function mapEnabledTaskTypes(types) {
  if (!types) return {};
  if (typeof types === 'object' && !Array.isArray(types)) return types;
  if (Array.isArray(types)) {
    const obj = {};
    types.forEach((t) => { obj[t] = true; });
    return obj;
  }
  return {};
}

/**
 * Transform /decoupled-api/node/task/ responses → task objects.
 *
 * Collections return **syncable objects** compatible with DataSync:
 *   { submission_url, delete_url, data: { id, title, columnId, … } }
 *
 * Single items (TaskEdit) return a flat object for backward compatibility.
 */
export const tasksProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/node/task/')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;

  if (Array.isArray(dataToProcess)) {
    return dataToProcess
      .filter(isDecoupledRow)
      .map((item) => mapTaskSyncable(item));
  }

  if (isDecoupledRow(dataToProcess)) {
    const mapped = mapTaskFlat(dataToProcess);
    mapped.comments = [];
    mapped.history = [];
    mapped.timeLogs = [];
    return mapped;
  }

  return dataToProcess;
};

/**
 * Resolve a status value (TID or label) to { label, columnId }.
 * Handles both decoupled (label) and decoupled_card (TID) display formats.
 *
 * Resolution order for TID inputs:
 *   1. _tidToLabel dynamic cache (built from the live API — survives DB resets)
 *   2. STATUS_TID_TO_LABEL legacy map (last-resort fallback, breaks after DB reset)
 * Column ID is always derived from the resolved label via STATUS_COLUMN_MAP /
 * SUB_STATUS_COLUMN_MAP (label-based, stable across DB resets).
 *
 * TODO Phase 2: once field_kanban_column is available from the API, read columnId
 * directly from _tidToColumn / _tidToSubColumn (populated by taskStatusMapsProcessor)
 * and remove STATUS_COLUMN_MAP / SUB_STATUS_COLUMN_MAP entirely.
 */
function resolveStatus(raw, isSubTask) {
  if (raw == null) return { label: 'Backlog', columnId: isSubTask ? 101 : 2 };

  const tidStr = String(raw);
  const isNumeric = /^\d+$/.test(tidStr);

  const label = isNumeric
    ? (_tidToLabel?.[tidStr] ?? STATUS_TID_TO_LABEL[parseInt(tidStr, 10)] ?? 'Backlog')
    : (decodeHtmlEntities(tidStr) || 'Backlog');

  const columnId = isSubTask
    ? (SUB_STATUS_COLUMN_MAP[label] ?? 101)
    : (STATUS_COLUMN_MAP[label] ?? 2);

  return { label, columnId };
}

/**
 * Route de la FICHE d'un contenu lié à une tâche (`field_linked_content`).
 *
 * Le back renvoie le détail de chaque contenu lié (`linkedContentRefs` :
 * id · bundle · libellé de type · titre, cf. TaskLinkedContentSubscriber) mais
 * PAS d'URL : les routes du SPA sont une connaissance du front (routes.js), pas
 * de Drupal. Un bundle absent d'ici reste affiché (type + titre) mais sans lien
 * — mieux qu'un lien qui tomberait sur une 404.
 */
const LINKED_CONTENT_ROUTES = {
  company: '/crm/companies/',
  contact: '/crm/contacts/',
  deal: '/crm/deals/',
  task: '/task-edit/',
  builder_page: '/docs/page/',
};

/**
 * Contenus liés d'une tâche, prêts à l'affichage (fiche ticket + carte kanban).
 *
 * Les types de contenu sont nommés par domaine (« CRM — Transaction »,
 * « Projet — Tâche ») : on ne garde que le segment de droite, seul utile en
 * face d'un titre — le domaine est déjà donné par l'écran où l'on se trouve.
 */
function mapLinkedContent(refs) {
  if (!Array.isArray(refs)) return [];
  return refs
    .filter((r) => r && r.id)
    .map((r) => {
      const label = String(r.bundleLabel || r.bundle || '');
      const prefix = LINKED_CONTENT_ROUTES[r.bundle];
      return {
        id: r.id,
        bundle: r.bundle || '',
        typeLabel: label.split(' — ').pop(),
        title: decodeHtmlEntities(r.title || ''),
        url: prefix ? `${prefix}${r.id}` : '',
      };
    });
}

function mapTaskData(d) {
  const isSubTask = !!d.parentTaskId;

  // field_task_state (workflow state machine ID) takes priority over the legacy
  // field_task_status (taxonomy TID) when both are present. When state is
  // present, columnId is the state machine ID (string) so the KanbanBoard can
  // map it directly to a workflow-driven column.
  const stateId = d.state || null;
  let columnId;
  let statusLabel;

  if (stateId) {
    columnId = stateId;
    statusLabel = stateId;
  } else {
    const resolved = resolveStatus(d.status, isSubTask);
    columnId = resolved.columnId;
    statusLabel = resolved.label;
  }

  return {
    id: d.id,
    title: decodeHtmlEntities(d.title),
    type: d.type != null ? String(d.type) : '',
    columnId,
    status: statusLabel,
    state: stateId,
    priority: d.priority || 0,  // Drupal write key — used by DataSync POST
    rating: d.priority || 0,   // Frontend display key — used by UI components
    complexity: d.complexity || 0,
    tags: d.tags || [],
    profile: d.profile || '',
    assignee: d.assignee || '',
    author: d.author || '',
    description: d.description || '',
    allocatedTime: d.allocatedTime || 0,
    timeSpent: d.timeSpent || 0,
    raf: d.raf || 0,
    dueDate: d.dueDate || '',
    isUrgent: d.isUrgent === true || d.isUrgent === 1 || d.isUrgent === '1',
    // Strict boolean: the API omits the key (undefined) or sends "0" for
    // non-internal tasks; only a real true/1/"1" marks it internal.
    internal: d.internal === true || d.internal === 1 || d.internal === '1',
    estimation: d.estimation || d.allocatedTime || 0,
    parentId: d.parentTaskId ? parseInt(d.parentTaskId, 10) : undefined,
    projectId: d.projectId ? parseInt(d.projectId, 10) : undefined,
    demande_evolution: d.subType || d.returnType || '',
    returnType: d.returnType || '',
    evolutionType: d.evolutionType || '',
    release: d.release || '',
    budget: d.budget || '',
    rank: d.rank || null,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    // Base field `changed` du noeud, exposé par le view display decoupled_card.
    // Ce mappeur est une LISTE BLANCHE : une clé absente ici n'atteint jamais les
    // composants, même si l'API la renvoie.
    changed: d.changed || '',
    archived: d.archived || null,
    // Contenus liés (entreprise, transaction, contact…) détaillés par le back
    // (linkedContentRefs) : type + titre + fiche, pour l'en-tête du ticket et
    // la carte kanban. `linkedContent` reste la liste de nids, écrite telle
    // quelle par le save — on ne la remplace pas.
    linkedContentRefs: mapLinkedContent(d.linkedContentRefs),
    // Identifiant lisible (« ORB-14 ») ajouté en lecture par le module orbit_ref.
    // Absent de la réponse tant que le projet n'a pas de clé : '' = masqué.
    ref: d.ref || '',
    // Son lien partageable (…/api/orbit/ref/ORB-14/go), absent sans clé ou
    // sans URL du front connue côté Drupal.
    refUrl: d.refUrl || '',
    attachedFiles: d.attachedFiles || [],
    // Technical context of a report (URL, browser, JS errors). Rendered as an
    // OBJECT by the decoupled_json formatter. Like every other key here this
    // mapper is an explicit allow-list: leaving it out drops it silently,
    // however well the API exposes it.
    reportContext: d.reportContext || null,
  };
}

function mapTaskSyncable(item) {
  return {
    // `item_url` is the syncable IDENTITY for DataSync (`item_url ?? submission_url`).
    // Dropping it made an identity APPEAR on the first save (the response carries
    // `item_url`): DataSync reads that as a server reload and swallows the next
    // edit without POSTing it.
    item_url: item.item_url,
    submission_url: item.submission_url,
    delete_url: item.delete_url,
    data: mapTaskData(fieldsOf(item)),
  };
}

function mapTaskFlat(item) {
  return {
    ...mapTaskData(fieldsOf(item)),
    _meta: {
      itemUrl: item.item_url,
      submissionUrl: item.submission_url,
      deleteUrl: item.delete_url,
    },
  };
}

/**
 * Transform /decoupled-api/comment/task_comment/collection → comments array
 */
export const commentsProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/comment/task_comment/')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;
  if (!Array.isArray(dataToProcess)) return dataToProcess;

  return dataToProcess
    .filter(isDecoupledRow)
    .map((item) => {
      const d = fieldsOf(item);
      return {
        id: d.id,
        author: d.author || '',
        date: d.date || '',
        text: stripHtml(d.text),
        rawHtml: d.text || '',
        subject: d.subject || '',
        entityId: d.entityId ? parseInt(d.entityId, 10) : null,
      };
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));
};

/**
 * Transform /decoupled-api/comment/task_history/collection → history array
 */
export const historyProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/comment/task_history/')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;
  if (!Array.isArray(dataToProcess)) return dataToProcess;

  return dataToProcess
    .filter(isDecoupledRow)
    .map((item) => {
      const d = fieldsOf(item);
      return {
        id: d.id,
        user: d.author || '',
        // Priority: human-readable text (comment_body) → technical field_action
        // → subject as last resort. Never use subject first: it is now an ISO
        // date used as a sortable title, not a display string.
        action: decodeHtmlEntities(stripHtml(d.text)) || d.action || d.subject || '',
        date: d.date || '',
        entityId: d.entityId ? parseInt(d.entityId, 10) : null,
      };
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));
};

/**
 * Transform /decoupled-api/node/time_log/collection → timeLogs array
 * TODO: Remove — time logs now use raw syncable objects via DataSync; this flattening breaks compatibility.
 */
export const timeLogsProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/node/time_log/')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;
  if (!Array.isArray(dataToProcess)) return dataToProcess;

  return dataToProcess
    .filter(isDecoupledRow)
    .map((item) => {
      const d = fieldsOf(item);
      return {
        id: d.id,
        date: d.date || '',
        user: d.user || '',
        description: d.description || '',
        timeSpent: d.timeSpent || 0,
        timeLeft: d.timeLeft || 0,
        taskId: d.taskId ? parseInt(d.taskId, 10) : null,
        projectId: d.projectId ? parseInt(d.projectId, 10) : null,
        createdAt: d.createdAt,
      };
    })
    .sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt));
};

/**
 * Transform /decoupled-api/taxonomy_term/task_type/collection responses.
 *
 * Returns items with value/label (for backward-compat with typeOptions) PLUS
 * a `data` sub-object preserving id, name, parentId (0 = top-level type,
 * positive integer = subtype), and the inline workflow object
 * (workflowId, workflowStates, workflowTransitions) exposed by the
 * decoupled_workflow_states formatter.
 */
export const taskTypeCollectionProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/taxonomy_term/task_type/')) return dataToProcess;
  if (!requestContext.url.includes('/collection')) return dataToProcess;
  if (responseContext.status >= 400 || !Array.isArray(dataToProcess)) return dataToProcess;

  return dataToProcess
    .filter(item => item != null)
    .map(item => {
      const d = item.data ?? item;
      return {
        value: String(d.id ?? ''),
        label: decodeHtmlEntities(d.name) || '',
        data: {
          id: d.id ?? null,
          name: d.name || '',
          parentId: d.parentId ?? 0,
          workflow: d.workflow ?? null,
        },
      };
    });
};

/**
 * Transform taxonomy term collections into SelectField options.
 * Matches any /decoupled-api/taxonomy_term/{vocab}/collection URL,
 * EXCEPT public_holiday, custom_task_type, and task_type which have dedicated processors,
 * and EXCEPT any URL carrying `rawSyncable=1` — a screen that edits terms rather than
 * offering them as options (cf. RAW_SYNCABLE_PARAM).
 */
export const taxonomyToOptionsProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/taxonomy_term/')) return dataToProcess;
  if (!requestContext.url.includes('/collection')) return dataToProcess;
  // An editing screen asked for the Syncable Objects themselves.
  if (wantsRawSyncable(requestContext.url)) return dataToProcess;
  if (requestContext.url.includes('/doc_directory/')) return dataToProcess;
  if (requestContext.url.includes('/doc_space_category/')) return dataToProcess;
  if (requestContext.url.includes('/public_holiday/')) return dataToProcess;
  if (requestContext.url.includes('/custom_task_type/')) return dataToProcess;
  if (requestContext.url.includes('/task_type/')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;
  if (!Array.isArray(dataToProcess)) return dataToProcess;

  return dataToProcess
    .filter((item) => item != null)
    .map((item) => {
      const d = item.data ?? item;
      return {
        value: String(d.id ?? d.tid ?? ''),
        label: decodeHtmlEntities(d.name) || '',
      };
    });
};

/**
 * Transform /decoupled-api/taxonomy_term/public_holiday/collection
 * into holiday objects preserving date, name, and recurring flag.
 */
export const publicHolidaysProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/taxonomy_term/public_holiday/')) return dataToProcess;
  if (!requestContext.url.includes('/collection')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;
  if (!Array.isArray(dataToProcess)) return dataToProcess;

  return dataToProcess
    .filter((item) => item != null)
    .map((item) => {
      const d = item.data ?? item;
      return {
        id: String(d.id ?? d.tid ?? ''),
        name: d.name || '',
        date: d.date || '',
        recurring: d.recurring ?? false,
      };
    });
};

/**
 * Transform /decoupled-api/taxonomy_term/custom_task_type/collection
 * into task-type objects preserving color and default_duration.
 */
export const customTaskTypesProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/taxonomy_term/custom_task_type/')) return dataToProcess;
  if (!requestContext.url.includes('/collection')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;
  if (!Array.isArray(dataToProcess)) return dataToProcess;

  return dataToProcess
    .filter((item) => item != null)
    .map((item) => {
      const d = item.data ?? item;
      return {
        id: String(d.id ?? d.tid ?? ''),
        name: d.name || '',
        color: d.color || '',
        defaultDuration: d.default_duration ?? d.defaultDuration ?? null,
      };
    });
};

/**
 * Transform /decoupled-api/node/assignment/ responses into DayPilot event objects.
 */
export const assignmentsProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/node/assignment/')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;
  if (!Array.isArray(dataToProcess)) return dataToProcess;

  return dataToProcess
    .filter(isDecoupledRow)
    .map((item) => {
      const d = fieldsOf(item);
      return {
        id: d.id,
        text: decodeHtmlEntities(d.title) || '',
        start: d.start,
        end: d.end,
        resource: String(d.resource || ''),
        backColor: d.color || '#6fa8dc',
        toolTip: buildAssignmentTooltip(d),
        _meta: {
          taskId: d.taskId ? parseInt(d.taskId, 10) : null,
          projectId: d.projectId ? parseInt(d.projectId, 10) : null,
          customTaskType: d.customTaskType || '',
          notes: d.notes || '',
          author: d.author || '',
          workload: (() => { const w = parseInt(d.workload, 10); return Number.isFinite(w) ? w : 100; })(),
          submissionUrl: item.submission_url,
          deleteUrl: item.delete_url,
        },
      };
    });
};

function buildAssignmentTooltip(d) {
  const parts = [];
  if (d.title) parts.push(decodeHtmlEntities(d.title));
  if (d.notes) parts.push(stripHtml(d.notes));
  return parts.join(' — ') || '';
}

/**
 * Transform node/account collections into SelectField options.
 * Matches /decoupled-api/node/account/collection URLs.
 */
export const accountsToOptionsProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/node/account/')) return dataToProcess;
  if (!requestContext.url.includes('/collection')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;
  if (!Array.isArray(dataToProcess)) return dataToProcess;

  return dataToProcess
    .filter((item) => item != null)
    .map((item) => {
      const d = item.data ?? item;
      return {
        value: String(d.id ?? ''),
        label: decodeHtmlEntities(d.name) || '',
      };
    });
};

/**
 * Transform /decoupled-api/taxonomy_term/doc_directory/ responses → directory objects.
 *
 * When the URL has no parent filter (e.g. doc-hub spaces), filter to root terms only
 * (parentId 0, null, or empty). decoupled_toolbox returns 400 for filter parent=0.
 */
export const docDirectoriesProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/taxonomy_term/doc_directory/')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;

  const hasParentFilter = /filter\[0\]\[f\]=parent/.test(requestContext.url);
  const wantsFullTree = /_tree=1/.test(requestContext.url);
  const isRootTerm = (d) => {
    const pid = d.parentId;
    return pid == null || pid === 0 || pid === '0' || (Array.isArray(pid) && pid.length === 0);
  };

  // Les deux formes (cf. fieldsOf) : un espace où l'on n'a que « Voir » arrive
  // À PLAT — le filtrer sur `item.data` le retirait du hub et laissait sa fiche
  // brute (description en HTML échappé).
  if (Array.isArray(dataToProcess)) {
    let items = dataToProcess.filter(isDecoupledRow);
    if (!hasParentFilter && !wantsFullTree) {
      items = items.filter((item) => isRootTerm(fieldsOf(item)));
    }
    return items.map((item) => mapDocDirectory(item));
  }
  if (isDecoupledRow(dataToProcess)) {
    return mapDocDirectory(dataToProcess);
  }
  return dataToProcess;
};

// `field_description` est un `text_long` : l'API en rend le HTML FILTRÉ
// (« <p>…</p> »). Or le module doc l'édite et l'affiche comme du texte simple
// (formulaire d'espace, cartes de /docs, arborescence) — sans ce nettoyage, les
// balises se lisent telles quelles à l'écran.
const plainText = (html) =>
  decodeHtmlEntities(String(html ?? '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ')).trim();

function mapDocDirectory(item) {
  const d = fieldsOf(item);
  const rawParent = d.parentId;
  const parentId = Array.isArray(rawParent)
    ? (rawParent[0] ? parseInt(rawParent[0], 10) : 0)
    : (rawParent ? parseInt(rawParent, 10) : 0);
  return {
    id: d.id,
    name: d.name,
    description: plainText(d.description),
    projectId: d.projectId ? parseInt(d.projectId, 10) : null,
    parentId: parentId || 0,
    weight: d.weight || 0,
    ownerId: d.ownerId ? parseInt(d.ownerId, 10) : 0,
    accessGrants: d.accessGrants || [],
    overrideAccess: d.overrideAccess || false,
    spaceCategoryIds: Array.isArray(d.spaceCategoryIds) ? d.spaceCategoryIds.map(Number) : [],
    wikiGroupIds: Array.isArray(d.wikiGroupIds) ? d.wikiGroupIds.map(Number) : [],
    // Les MÊMES références, en CHAÎNES : ce sont elles que compare le filtrage
    // déclaratif de /docs (DataFilter + FacetList). Les conditions YAML
    // (`is`, `contains`) comparent en `isEqual` STRICT, et la valeur d'une
    // facette est toujours une chaîne (cf. collectionToFacetProcessor) — un id
    // numérique n'y correspondrait jamais, sans erreur ni trace. Les clés
    // numériques ci-dessus restent intactes pour les consommateurs React.
    projectKey: d.projectId ? String(parseInt(d.projectId, 10)) : '',
    // LIBELLÉS des entités référencées, calculés côté back (orbit_doc :
    // DocDirectoryCollectionSubscriber) : le display decoupled ne rend que des
    // ids, et le YAML ne sait pas indexer une table de libellés par une clé
    // dynamique. Les listes sont des objets `{value}` — la seule forme qu'un
    // `Switch` reactive-json sache parcourir.
    projectName: d.projectName || '',
    spaceCategoryNames: Array.isArray(d.spaceCategoryNames) ? d.spaceCategoryNames : [],
    wikiGroupNames: Array.isArray(d.wikiGroupNames) ? d.wikiGroupNames : [],
    categoryKeys: Array.isArray(d.spaceCategoryIds) ? d.spaceCategoryIds.map(String) : [],
    wikiGroupKeys: Array.isArray(d.wikiGroupIds) ? d.wikiGroupIds.map(String) : [],
    archived: d.archived || null,
    spaceUrl: `/docs/space/${d.id}`,
    _meta: {
      itemUrl: item.item_url,
      submissionUrl: item.submission_url,
      deleteUrl: item.delete_url,
    },
  };
}

/**
 * Transform /decoupled-api/taxonomy_term/doc_space_category/ responses
 * into flat category objects preserving parent hierarchy.
 */
export const docSpaceCategoriesProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/taxonomy_term/doc_space_category/')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;

  if (Array.isArray(dataToProcess)) {
    return dataToProcess.filter((item) => item?.data ?? item?.id != null).map((item) => {
      const d = item.data ?? item;
      const rawParent = d.parentId;
      const parentId = Array.isArray(rawParent)
        ? (rawParent[0] ? parseInt(rawParent[0], 10) : 0)
        : (rawParent ? parseInt(rawParent, 10) : 0);
      return { id: d.id, name: d.name, parentId, weight: d.weight || 0, description: d.description || '' };
    });
  }
  return dataToProcess;
};

/**
 * Une page du WIKI, par opposition à une page de SITE.
 *
 * Les deux sont des `builder_page` servies par la même URL decoupled ; seule
 * la page documentaire est rangée dans un répertoire (`field_directory`, dont
 * le presave déduit `field_root_directory`). Le remodelage ci-dessous est fait
 * pour le wiki — il ne conserve pas `blocs`, `slug` ni `modelFor` — donc on
 * le refuse à ce qui n'est pas une page rangée : un écran du builder qui
 * lirait cette URL récupère la réponse intacte plutôt que mutilée.
 */
function isWikiPage(d) {
  return !!(d && (d.directoryId || d.rootDirectoryId));
}

/**
 * Transform /decoupled-api/node/builder_page/ responses → doc page objects.
 */
export const docPagesProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/node/builder_page/')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;

  // Les deux formes (cf. fieldsOf) : une page en lecture seule arrive À PLAT —
  // la filtrer sur `item.data` vidait l'espace (« Cet espace est vide »), et la
  // page seule, non remise en forme, perdait son `_readOnly.slug` : aucun bloc.
  if (Array.isArray(dataToProcess)) {
    return dataToProcess
      .filter(isDecoupledRow)
      .map((item) => (isWikiPage(fieldsOf(item)) ? mapDocPageFlat(item) : item));
  }
  if (isDecoupledRow(dataToProcess)) {
    return isWikiPage(fieldsOf(dataToProcess)) ? mapDocPageSyncable(dataToProcess) : dataToProcess;
  }
  return dataToProcess;
};

function mapDocPageData(d) {
  return {
    id: d.id,
    title: decodeHtmlEntities(d.title),
    summary: d.summary || '',
    rootDirectoryId: d.rootDirectoryId ? parseInt(d.rootDirectoryId, 10) : null,
    directoryId: d.directoryId ? parseInt(d.directoryId, 10) : null,
    weight: d.weight || 0,
    category: d.category || '',
    // `tags` est pris par les catégories de site du builder : les étiquettes
    // documentaires arrivent sous `docTags`.
    docTags: [].concat(d.docTags || []),
    relatedDocs: [].concat(d.relatedDocs || []).map((id) => parseInt(id, 10)).filter(Number.isFinite),
    relatedTasks: [].concat(d.relatedTasks || []).map((id) => parseInt(id, 10)).filter(Number.isFinite),
    owner: d.owner || '',
    isTemplate: d.isTemplate || false,
    links: d.links || [],
    icon: d.icon || '',
    // Le display ne sert plus un NOM d'auteur mais son identifiant.
    authorUid: d.authorUid || null,
    moderationState: d.moderationState || 'draft',
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    pageUrl: `/docs/page/${d.id}`,
    // LIBELLÉS calculés côté back (orbit_doc : DocPageOutputSubscriber) : le
    // display ne rend que des identifiants, et le YAML ne sait pas résoudre un
    // id en nom. Sans eux, le tableau de bord d'un espace ne pourrait afficher
    // ni la colonne « Répertoire » ni la colonne « Auteur ».
    directoryName: d.directoryName || '',
    authorName: d.authorName || '',
    // La MÊME référence, en chaîne : c'est elle que compare le filtrage
    // déclaratif (DataFilter + FacetList). Les conditions YAML comparent en
    // strict et une valeur de facette est toujours une chaîne — un id numérique
    // n'y correspondrait jamais, sans erreur ni trace.
    directoryKey: d.directoryId ? String(parseInt(d.directoryId, 10)) : '',
  };
}

function mapDocPageFlat(item) {
  return {
    ...mapDocPageData(fieldsOf(item)),
    _meta: {
      itemUrl: item.item_url,
      submissionUrl: item.submission_url,
      deleteUrl: item.delete_url,
    },
  };
}

function mapDocPageSyncable(item) {
  const d = fieldsOf(item);
  return {
    submission_url: item.submission_url,
    delete_url: item.delete_url,
    data: {
      id: d.id,
      title: decodeHtmlEntities(d.title),
      summary: d.summary || '',
      rootDirectoryId: d.rootDirectoryId ? parseInt(d.rootDirectoryId, 10) : null,
      directoryId: d.directoryId ? parseInt(d.directoryId, 10) : null,
      weight: d.weight || 0,
      relatedDocs: [].concat(d.relatedDocs || []).map((id) => parseInt(id, 10)).filter(Number.isFinite),
      relatedTasks: [].concat(d.relatedTasks || []).map((id) => parseInt(id, 10)).filter(Number.isFinite),
      isTemplate: d.isTemplate || false,
      links: d.links || [],
      icon: d.icon || '',
      revisionLog: d.revisionLog || '',
    },
    _readOnly: {
      authorUid: d.authorUid || null,
      category: d.category || '',
      owner: d.owner || '',
      moderationState: d.moderationState || 'draft',
      docTags: d.docTags || [],
      createdAt: d.createdAt,
      updatedAt: d.updatedAt,
      pageUrl: `/docs/page/${d.id}`,
      // Le CORPS de la page est une liste de blocs, montée par
      // `BuilderPageMount` — qui désigne la page par son SLUG. Sans cette clé,
      // le montage ne reçoit rien et la page s'affiche VIDE, sans erreur.
      // En lecture seule à dessein : le slug d'une page du wiki est fabriqué
      // par le back (`wiki-…`), il n'a pas à repartir dans le DataSync des
      // métadonnées.
      slug: d.slug || '',
    },
  };
}

/**
 * Transform /decoupled-api/comment/doc_comment/ responses → doc comment objects.
 */
export const docCommentsProcessor = ({ requestContext, responseContext, dataToProcess }) => {
  if (!requestContext.url.includes('/decoupled-api/comment/doc_comment/')) return dataToProcess;
  if (responseContext.status >= 400) return dataToProcess;
  if (!Array.isArray(dataToProcess)) return dataToProcess;

  return dataToProcess
    .filter(isDecoupledRow)
    .map((item) => {
      const d = fieldsOf(item);
      return {
        id: d.id,
        body: d.body || '',
        author: d.author || '',
        pageId: d.pageId ? parseInt(d.pageId, 10) : null,
        createdAt: d.createdAt,
        updatedAt: d.updatedAt,
      };
    })
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};
